// Global
import React, { useEffect } from 'react';
import {
  Field,
  Item,
  LinkField,
  ImageField,
  RichTextField,
} from '@sitecore-jss/sitecore-jss-nextjs';

// Marquee helpers
import { useMarqueeCarousel } from '../useMarqueeCarousel';
import { CarouselMarqueeProps } from '../marqueeTypes';
import { DotButton, useDotButton } from '../MarqueeDotButton';
import { PrevButton, NextButton, usePrevNextButtons } from '../MarqueeArrowButton';

// Other Helpers
import RichTextA11yWrapper from 'helpers/RichTextA11yWrapper/RichTextA11yWrapper';
import ImageWrapper from 'helpers/ImageWrapper/ImageWrapper';
import LinkWrapper from 'helpers/LinkWrapper/LinkWrapper';
import { useLoginContext } from 'helpers/Providers/LoginContextProvider';
import { Bok } from 'lib/templates/Foundation.BOKF.model';

const HeroMarquee = (props: CarouselMarqueeProps): JSX.Element => {
  const {
    marquees,
    width,
    emblaRef,
    emblaApi,
    enableCarousel,
    loginMarqueeRef,
    onNavButtonClick,
    toggleAutoplay,
  } = useMarqueeCarousel(props);

  const { isMarqueeLoginEnabled, setMarqueeLoginEnabled } = useLoginContext();
  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(emblaApi, onNavButtonClick);
  const { prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick } =
    usePrevNextButtons(emblaApi, onNavButtonClick);

  // Login Effects
  useEffect(() => {
    setMarqueeLoginEnabled(props?.fields?.EnableLoginBox?.value as boolean);
  }, [setMarqueeLoginEnabled, props?.fields?.EnableLoginBox?.value]);

  useEffect(() => {
    if (isMarqueeLoginEnabled) {
      const loginElement = document.querySelector('[data-component="login"]');
      if (loginElement && loginMarqueeRef.current) {
        loginMarqueeRef.current.appendChild(loginElement);
      }
    }
  }, [loginMarqueeRef, isMarqueeLoginEnabled]);

  // Remove visibility hidden from slides once Embla is initialized
  useEffect(() => {
    if (emblaApi) {
      const slides = document.querySelectorAll('.carousel-marquee-slide');
      slides.forEach((slide) => {
        (slide as HTMLElement).style.visibility = 'visible';
      });
    }
  }, [emblaApi]);

  const { fields = undefined } = props;
  if (!fields || marquees?.length === 0) {
    return <></>;
  }

  return (
    <section className={`component carousel-marquee ${width}`} data-component="marquee">
      <div className="flex">
        <div className="carousel-marquee-viewport group" ref={emblaRef}>
          <div className="carousel-marquee-container">
            {marquees?.map((item, index) => {
              const isFirstSlide = index === 0;
              return (
                <div
                  className="carousel-marquee-slide max-h-[424px] lg:max-h-[500px]"
                  key={item.id}
                  data-id={item.id}
                  style={!isFirstSlide ? { visibility: 'hidden' } : undefined}
                >
                  {/* Desktop Image */}
                  <ImageWrapper
                    field={item?.fields?.DesktopImage as ImageField}
                    className="hidden lg:block min-h-[500px] w-full object-cover object-left"
                    priority={isFirstSlide}
                    sizes="100vw"
                  />
                  {/* Tablet Image */}
                  <ImageWrapper
                    field={
                      (item?.fields?.TabletImage as ImageField)?.value
                        ? (item?.fields?.TabletImage as ImageField)
                        : (item?.fields?.DesktopImage as ImageField)
                    }
                    className="hidden sm:block lg:hidden min-h-[424px] w-full object-cover object-center"
                    priority={isFirstSlide}
                    sizes="100vw"
                  />
                  {/* Mobile Image */}
                  {(() => {
                    let mobileImageField: ImageField;
                    if ((item?.fields?.MobileImage as ImageField)?.value) {
                      mobileImageField = item?.fields?.MobileImage as ImageField;
                    } else if ((item?.fields?.TabletImage as ImageField)?.value) {
                      mobileImageField = item?.fields?.TabletImage as ImageField;
                    } else {
                      mobileImageField = item?.fields?.DesktopImage as ImageField;
                    }
                    return (
                      <ImageWrapper
                        field={mobileImageField}
                        className="block sm:hidden min-h-[424px] w-full object-cover"
                        priority={isFirstSlide}
                        sizes="100vw"
                      />
                    );
                  })()}
                  <div className="flex justify-start items-center absolute top-0 left-0 bottom-0 w-full hero-experience">
                    <div className="container">
                      <div className="marquee-content-container my-auto sm:mx-4 md:ml-8 lg:ml-10">
                        <RichTextA11yWrapper
                          field={item?.fields?.Title as RichTextField}
                          tag={
                            (((item?.fields?.TitleTag as Item)?.fields?.Tag as Field)
                              ?.value as string) || 'h1'
                          }
                          className={`pb-2 ${
                            ((item?.fields?.LightModeToggle as Field)?.value as boolean)
                              ? 'text-light-fill-inverse'
                              : 'text-light-fill-primary'
                          }`}
                        ></RichTextA11yWrapper>

                        {(() => {
                          const borderStyle =
                            ((item?.fields?.TitleBorderStyle as Item)?.fields?.Value as Field)
                              ?.value || 'bg-light-background-secondary-brand';
                          return <div className={`w-[48px] h-[4px] ${borderStyle}`}></div>;
                        })()}

                        <RichTextA11yWrapper
                          field={item?.fields?.RichText as RichTextField}
                          className={`py-6 rtaw ${
                            ((item?.fields?.LightModeToggle as Field)?.value as boolean)
                              ? 'text-light-fill-inverse'
                              : 'text-light-fill-primary'
                          }`}
                        ></RichTextA11yWrapper>

                        <div className="grid grid-cols-1 md:flex gap-4">
                          {item?.fields?.ButtonOne && (
                            <LinkWrapper
                              field={item?.fields?.ButtonOne as LinkField}
                              className={
                                (item?.fields?.ButtonOneStyle as Bok.BaseTypes.Selector)?.fields
                                  ?.Value?.value
                              }
                            />
                          )}
                          {item?.fields?.ButtonTwo && (
                            <LinkWrapper
                              field={item?.fields?.ButtonTwo as LinkField}
                              className={
                                (item?.fields?.ButtonTwoStyle as Bok.BaseTypes.Selector)?.fields
                                  ?.Value?.value
                              }
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div
            className={`previous-button-container ${
              !enableCarousel ? 'hidden' : ''
            } opacity-0 group-hover:opacity-100`}
          >
            <PrevButton
              className="previous-button"
              onClick={onPrevButtonClick}
              disabled={prevBtnDisabled}
            />
          </div>
          <div
            className={`next-button-container ${
              !enableCarousel ? 'hidden' : ''
            } opacity-0 group-hover:opacity-100`}
          >
            <NextButton
              className="next-button"
              onClick={onNextButtonClick}
              disabled={nextBtnDisabled}
            />
          </div>
        </div>
        {isMarqueeLoginEnabled && (
          <div className="carousel-marquee-login" ref={loginMarqueeRef}></div>
        )}
      </div>
      <div
        className={`carousel-marquee-controls ${
          !enableCarousel ? 'hidden' : ''
        } relative bg-transparent`}
      >
        <div className="flex flex-row h-[44px] justify-center items-center px-4 md:px-6 stroke-light-stroke-primary bg-light-background-neutral-variant border absolute left-0">
          <label className="carousel-toggle inline-flex items-center mr-2 w-auto">
            <input type="checkbox" onClick={toggleAutoplay} aria-label="Pause Carousel" />
            <span className="carousel-slider w-[46px] bg-light-background-neutral-highest"></span>
            <div className="p2 ps-2 ms-12 text-light-fill-primary-variant">Pause Carousel</div>
          </label>
        </div>
        <div className="carousel-marquee-dots">
          {scrollSnaps.map((_, index) => (
            <DotButton
              key={marquees?.[index]?.id ?? index}
              data-id={marquees?.[index]?.id ?? index}
              onClick={() => onDotButtonClick(index)}
              className={'carousel-marquee-dot'.concat(
                index === selectedIndex ? ' carousel-marquee-dot--selected' : ''
              )}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroMarquee;
